import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { financeService } from '../services/api';
import MessagesSidebar from '../components/MessagesSidebar';

/* ─── sabitler ─── */
const TYPE_LABELS = {
    1: { fa: 'پیش‌پرداخت', en: 'Advance Payment' },
    2: { fa: 'چک‌های ضمانت', en: 'Guarantee Checks' },
    3: { fa: 'پرداخت نهایی', en: 'Final Payment' }
};

// paid + status → { fa, en, className }
const STATUS_MAP = {
    'false-1': { fa: 'در انتظار پرداخت', en: 'Awaiting Payment', className: 'bg-amber-100 text-amber-700' },
    'true-1': { fa: 'در انتظار تایید', en: 'Awaiting Approval', className: 'bg-blue-100 text-blue-700' },
    'true-2': { fa: 'تایید شده', en: 'Approved', className: 'bg-green-100 text-green-700' },
    'true-3': { fa: 'عدم تایید', en: 'Rejected', className: 'bg-red-100 text-red-700' }
};

const getStatusInfo = (paid, status) => STATUS_MAP[`${paid}-${status}`] || null;

// دکمه پرداخت/چک فعال است اگر: (paid=false و status=1) یا (status=3)
const isActionEnabled = (paid, status) => (!paid && status === 1) || status === 3;

/* ─── küçük UI bileşenleri ─── */
const StatusBadge = ({ paid, status, t }) => {
    const info = getStatusInfo(paid, status);
    if (!info) return <span className="text-gray-400 text-xs">—</span>;
    return (
        <span className={`inline-block px-2.5 py-1 rounded-lg text-[11px] sm:text-xs font-bold whitespace-nowrap ${info.className}`}>
            {t(info.fa, info.en)}
        </span>
    );
};

const FileDownloadButton = ({ url, label }) => (
    <a
        href={url || undefined}
        target="_blank"
        rel="noopener noreferrer"
        aria-disabled={!url}
        onClick={(e) => { if (!url) e.preventDefault(); }}
        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] sm:text-xs font-bold border transition-colors whitespace-nowrap ${url
                ? 'border-[#204286] text-[#204286] hover:bg-[#EBF1FB] cursor-pointer'
                : 'border-gray-200 text-gray-300 cursor-not-allowed'
            }`}
    >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M7 10l5 5 5-5M12 15V3" />
        </svg>
        {label}
    </a>
);

const ActionButton = ({ payment, t, navigate }) => {
    const enabled = isActionEnabled(payment.paid, payment.status);
    const isCheckType = payment.type === 2;
    const label = isCheckType
        ? t('بارگزاری چک‌های ضمانت', 'Upload Guarantee Checks')
        : t('ورود به صفحه پرداخت', 'Go to Payment');

    const handleClick = () => {
        if (!enabled) return;
        navigate(isCheckType ? `/finance/checks/${payment.code}` : `/finance/document/${payment.code}`);
    };

    return (
        <motion.button
            type="button"
            onClick={handleClick}
            disabled={!enabled}
            whileHover={enabled ? { scale: 1.03 } : {}}
            whileTap={enabled ? { scale: 0.97 } : {}}
            className={`px-3.5 py-1.5 rounded-lg text-[11px] sm:text-xs font-bold whitespace-nowrap transition-all duration-200 ${enabled
                    ? 'text-white bg-gradient-to-r from-[#292DD8] to-[#4B4FEE] hover:from-[#1f23a8] hover:to-[#3a3ecc] shadow-sm'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
        >
            {label}
        </motion.button>
    );
};

/* ─── main ─── */
const Finance = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const isRtl = language === 'fa';
    const t = (fa, en) => (isRtl ? fa : en);

    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPayments = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await financeService.getPayments();
                setPayments(data || []);
            } catch (err) {
                console.error('Payments fetch error:', err);
                setError(t('خطا در دریافت اطلاعات', 'Error loading payments'));
            } finally {
                setLoading(false);
            }
        };
        fetchPayments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatAmount = (amount) =>
        amount === null || amount === undefined ? '—' : Number(amount).toLocaleString(isRtl ? 'fa-IR' : 'en-US');

    const formatDate = (iso) => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleDateString(isRtl ? 'fa-IR' : 'en-US');
        } catch {
            return '—';
        }
    };

    return (
        <div
            className="max-w-[1136px] mx-auto px-3 sm:px-4 py-4 lg:py-8 font-iransansRegular overflow-hidden"
            dir={isRtl ? 'rtl' : 'ltr'}
        >
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                <MessagesSidebar
                    activeKey="finance"
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />

                <div className="flex-1 w-full min-w-0">
                    {/* Title */}
                    <motion.h1
                        className="text-xl sm:text-2xl lg:text-[32px] font-extrabold text-[#292DD8] mb-5 lg:mb-7 text-right"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {t('امورمالی', 'Financial Affairs')}
                    </motion.h1>

                    {/* Loading */}
                    {loading && (
                        <div className="flex justify-center items-center min-h-[40vh]">
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-10 h-10 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin" />
                                <p className="text-[#292DD8] font-bold text-sm">
                                    {t('در حال بارگذاری...', 'Loading...')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                        <div className="flex justify-center items-center min-h-[40vh]">
                            <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-8">
                                <p className="text-red-600 font-bold text-sm">{error}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="px-5 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 transition-colors"
                                >
                                    {t('تلاش مجدد', 'Try Again')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Empty */}
                    {!loading && !error && payments.length === 0 && (
                        <div className="flex justify-center items-center min-h-[40vh]">
                            <p className="text-gray-400 text-sm font-bold">
                                {t('هیچ پرداختی یافت نشد', 'No payments found')}
                            </p>
                        </div>
                    )}

                    {/* Table */}
                    {!loading && !error && payments.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="bg-[#F5F7FB] rounded-2xl overflow-hidden"
                        >
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[860px] text-right">
                                    {/* Tablo header - iki kolon yerine tek kolon */}
                                    <thead>
                                        <tr className="border-b border-[#D6E1F5]">
                                            <th className="px-4 py-3 text-[11px] sm:text-xs font-bold text-[#204286] whitespace-nowrap">
                                                {t('کد پیگیری', 'Tracking Code')}
                                            </th>
                                            <th className="px-4 py-3 text-[11px] sm:text-xs font-bold text-[#204286] whitespace-nowrap">
                                                {t('نوع', 'Type')}
                                            </th>
                                            <th className="px-4 py-3 text-[11px] sm:text-xs font-bold text-[#204286] whitespace-nowrap">
                                                {t('مبلغ', 'Amount')}
                                            </th>
                                            <th className="px-4 py-3 text-[11px] sm:text-xs font-bold text-[#204286] whitespace-nowrap">
                                                {t('زمان', 'Date')}
                                            </th>
                                            <th className="px-4 py-3 text-[11px] sm:text-xs font-bold text-[#204286] whitespace-nowrap">
                                                {t('وضعیت', 'Status')}
                                            </th>
                                            <th className="px-4 py-3 text-[11px] sm:text-xs font-bold text-[#204286] whitespace-nowrap">
                                                {t('فاکتور', 'Invoice')}
                                            </th>
                                            <th className="px-4 py-3 text-[11px] sm:text-xs font-bold text-[#204286] whitespace-nowrap">
                                                {t('پرداخت', 'Payment')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((payment) => (
                                            <tr key={payment.code} className="border-b border-[#D6E1F5] last:border-b-0">
                                                <td className="px-4 py-3.5 text-xs sm:text-sm text-[#204286] font-medium whitespace-nowrap">
                                                    {payment.code}
                                                </td>
                                                <td className="px-4 py-3.5 text-xs sm:text-sm text-[#204286] whitespace-nowrap">
                                                    {t(TYPE_LABELS[payment.type]?.fa, TYPE_LABELS[payment.type]?.en) || '—'}
                                                </td>
                                                <td className="px-4 py-3.5 text-xs sm:text-sm text-[#204286] whitespace-nowrap">
                                                    {formatAmount(payment.amount)}
                                                </td>
                                                <td className="px-4 py-3.5 text-xs sm:text-sm text-[#204286] whitespace-nowrap">
                                                    {formatDate(payment.created_at)}
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <StatusBadge paid={payment.paid} status={payment.status} t={t} />
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    {/* YENİ: مشاهده butonu - type=2 ise pasif */}
                                                    <motion.button
                                                        type="button"
                                                        onClick={() => {
                                                            if (payment.type === 2) return; // چک‌های ضمانت için pasif
                                                            navigate(`/finance/invoice/${payment.code}`);
                                                        }}
                                                        disabled={payment.type === 2}
                                                        whileHover={payment.type !== 2 ? { scale: 1.03 } : {}}
                                                        whileTap={payment.type !== 2 ? { scale: 0.97 } : {}}
                                                        className={`px-3.5 py-1.5 rounded-lg text-[11px] sm:text-xs font-bold whitespace-nowrap transition-all duration-200 ${payment.type !== 2
                                                                ? 'text-white bg-gradient-to-r from-[#292DD8] to-[#4B4FEE] hover:from-[#1f23a8] hover:to-[#3a3ecc] shadow-sm cursor-pointer'
                                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                            }`}
                                                    >
                                                        {t('مشاهده', 'View')}
                                                    </motion.button>
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <ActionButton payment={payment} t={t} navigate={navigate} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Finance;